import { Box, Stack, Typography } from '@mui/material';
import {
  calculateUpgradePath,
  EscalationRule,
  formatDeadlineShort,
  formatResponseExpectation,
  formatRuleResponseLabel,
  HolidayWindow,
  matchEscalationRule,
  SERVICE_LEVEL_PRIORITY_ORDER,
  TimeWindow,
} from '@provision/sidekick-utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMemo } from 'react';
import { formatEnumLabel } from './escalation-utils';

dayjs.extend(relativeTime);

interface EscalationCardProps {
  rules: EscalationRule[];
  urgencyLevel?: string | null;
  serviceLevel: string;
  currentTime: Date;
  timezone?: string;
  /**
   * Partner's actual business hours. Used for deadline calculations (e.g., "by end of day").
   */
  businessHours?: TimeWindow[];
  /**
   * Hours during which partners accept escalation notifications.
   * Used for condition matching. Falls back to businessHours when not set.
   */
  escalationCutoffHours?: TimeWindow[];
  holidays?: HolidayWindow[];
  expanded: boolean;
  isPreLits?: boolean;
}

const OVERLINE_SX = {
  display: 'block',
  mb: 0.5,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.6875rem',
} as const;

export const EscalationCard = ({
  rules,
  urgencyLevel,
  serviceLevel,
  currentTime,
  timezone,
  businessHours,
  escalationCutoffHours,
  holidays,
  expanded,
  isPreLits,
}: EscalationCardProps) => {
  const memoizedContext = useMemo(
    () => ({
      currentTime,
      timezone,
      businessHours,
      escalationCutoffHours,
      holidays,
      isPreLits,
    }),
    [currentTime, timezone, businessHours, escalationCutoffHours, holidays, isPreLits],
  );

  const {
    match,
    expectation,
    upgradePath,
    currentExpectationText,
    upgradeText,
    isHighestTier,
  } = useMemo(() => {
    const matchedByRule = matchEscalationRule(rules, serviceLevel, memoizedContext);
    const path = calculateUpgradePath(rules, serviceLevel, memoizedContext).slice(0, 5);

    const currentOrder = SERVICE_LEVEL_PRIORITY_ORDER.indexOf(serviceLevel);
    const highest = currentOrder === SERVICE_LEVEL_PRIORITY_ORDER.length - 1;

    const expectationText = buildCurrentExpectationText(
      matchedByRule?.rule ?? null,
      serviceLevel,
    );
    const upText = buildUpgradeText(path, highest, urgencyLevel);

    return {
      match: matchedByRule,
      expectation: formatResponseExpectation(matchedByRule, serviceLevel, timezone),
      upgradePath: path,
      currentExpectationText: expectationText,
      upgradeText: upText,
      isHighestTier: highest,
    };
  }, [rules, serviceLevel, memoizedContext, urgencyLevel, timezone]);

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">{expectation}</Typography>
        {match?.deadline ? (
          <Typography variant="caption" color="text.secondary">
            ({dayjs(match.deadline).from(dayjs(currentTime), true)})
          </Typography>
        ) : null}
      </Stack>

      {urgencyLevel === 'urgent' ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, fontWeight: 500, fontSize: '0.8125rem' }}
        >
          Mention after-hours rates to the client before escalating
        </Typography>
      ) : null}

      {expanded ? (
        <Box sx={{ mt: 2 }}>
          {/* CURRENT EXPECTATION section */}
          <Typography variant="caption" color="text.primary" sx={OVERLINE_SX}>
            Current Expectation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, fontSize: '0.8125rem' }}
          >
            {currentExpectationText}
          </Typography>

          {/* IF CLIENT UPGRADES section */}
          <Typography variant="caption" color="text.primary" sx={OVERLINE_SX}>
            If Client Upgrades
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', fontSize: '0.8125rem' }}
          >
            {upgradeText}
          </Typography>

          {/* Upgrade path details */}
          {!isHighestTier && upgradePath.length > 0 ? (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {upgradePath
                .filter((u) => !u.unavailable)
                .map((upgrade) => (
                  <Typography
                    key={upgrade.serviceLevel}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.8125rem' }}
                  >
                    {upgrade.serviceLevel} → response by{' '}
                    {formatDeadlineShort(upgrade.deadline, timezone)}
                  </Typography>
                ))}
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

function buildCurrentExpectationText(
  rule: EscalationRule | null,
  serviceLevel: string,
): string {
  if (!rule) {
    return `Not available for ${serviceLevel} clients.`;
  }

  const parts: string[] = [];

  if (rule.serviceLevels === 'all') {
    parts.push('All service levels');
  } else if (rule.serviceLevels.length > 0) {
    parts.push(`${rule.serviceLevels.join(' or ')} clients`);
  } else {
    parts.push(`${serviceLevel} clients`);
  }

  const timeLabel = formatEnumLabel(rule.timeCondition.type);
  if (rule.timeCondition.type !== 'always') {
    parts.push(timeLabel.toLowerCase());
  }

  parts.push(`response ${formatRuleResponseLabel(rule)}`);

  return `${parts.join(', ')}.`;
}

function buildUpgradeText(
  upgradePath: { serviceLevel: string; unavailable: boolean; deadline: Date | null }[],
  isHighestTier: boolean,
  urgencyLevel?: string | null,
): string {
  if (isHighestTier) {
    return 'This client is already at the highest tier.';
  }

  const available = upgradePath.filter((u) => !u.unavailable);
  if (available.length === 0) {
    const urgency = (urgencyLevel ?? 'normal').toLowerCase();
    if (urgency === 'normal') {
      return 'Normal escalations have the same response time for all membership tiers.';
    }
    if (urgency === 'life-safety') {
      return 'Life safety response is the same for all membership tiers.';
    }
    return 'No improved response time available through tier upgrade.';
  }

  return `Upgrading the client's membership tier may improve response times:`;
}
